import { Candidate } from '@/core/collections/candidates/candidate.interface';
import { StoreState } from '@/core/models/store.interface';
import { candidateMapper } from '@/modules/candidates/mappers/candidate.mapper';
import { Module } from 'vuex';
import { ApiResource } from './../../../models/api-resource.interface';
import { CandidateService } from './candidate.service';
import { CANDIDATE_INCLUDES } from '../../candidates/candidate.service';

const service = new CandidateService();

export interface CandidateState {
   candidates?: ApiResource<Array<Candidate>>;
}

const CandidateStore: Module<CandidateState, StoreState> = {
   namespaced: true,
   state: {},
   getters: {
      candidates: (state) => state.candidates,
   },
   mutations: {
      ADD_CANDIDATES(state, candidates?: ApiResource<Array<Candidate>>) {
         const newCandidate = [...(state.candidates?.data || []), ...(candidates?.data || [])];
         state.candidates = {
            ...state.candidates,
            data: newCandidate,
            meta: { total: newCandidate.length, current_page: 1, last_page: 1 },
         } as ApiResource<Array<Candidate>>;
      },
      CLEAR_CANDIDATES(state) {
         state.candidates = undefined;
      },
      DELETE_CANDIDATES(state, candidates: Array<number>) {
         candidates.forEach((candidate) => {
            const newCandidates = state.candidates?.data.filter((c) => c.id !== candidate);
            state.candidates = { ...state.candidates, data: newCandidates, meta: { total: newCandidates?.length } } as ApiResource<
               Array<Candidate>
            >;
         });
      },
      UPDATE_CANDIDATES(state, candidates: Array<{ candidate_id: number; is_handled: boolean }>) {
         candidates.forEach((candidate) => {
            if (!state.candidates) return;
            const c_index = state.candidates?.data.findIndex((c) => c.id === candidate.candidate_id);
            if (c_index === -1) return;
            if (state.candidates.data[c_index]) state.candidates.data[c_index].is_handled = candidate.is_handled;
         });
      },
      UPDATE_CANDIDATE(state, candidate: Candidate) {
         if (!state.candidates) return;
         const c_index = state.candidates?.data.findIndex((c) => c.id === candidate.id);
         if (state.candidates.data[c_index]) {
            const listCandidate = state.candidates.data[c_index];
            const candidateFromServer = candidateMapper(candidate);

            const updatedCandidate = deepMergeObject(listCandidate, candidateFromServer);
            state.candidates.data.splice(c_index, 1, updatedCandidate);
         }
      },
   },
   actions: {
      async getCandidates(context, callListId) {
         const response = await service.getCandidates(callListId, {
            include: CANDIDATE_INCLUDES.list.includes.join(','),
            append: CANDIDATE_INCLUDES.list.appends.join(','),
            per_page: '999',
         });
         const mappedResponse = { ...response, data: response.data.map((c) => candidateMapper(c)) };
         context.commit('CLEAR_CANDIDATES');
         context.commit('ADD_CANDIDATES', mappedResponse);
         return mappedResponse.data;
      },

      async updateCandidates(context, { callListId, candidates }) {
         const response = await service.putCandidates(callListId, candidates);
         context.commit(
            'UPDATE_CANDIDATES',
            response.data.map((c) => ({ candidate_id: c.id, is_handled: c.is_handled })),
         );
      },

      async deleteCandidates(context, { callListId, candidates }) {
         await service.deleteCandidates(callListId, candidates);
         context.commit('DELETE_CANDIDATES', candidates.candidate_ids);
      },
      async updateCandidateHandled(context, { callListId, handeldCandidate }) {
         const response = await service.postCandidates(callListId, handeldCandidate);
         context.commit(
            'UPDATE_CANDIDATES',
            response.data.map((c) => ({ candidate_id: c.id, is_handled: c.is_handled })),
         );
      },
      async addCandidatesToCallList(_context, { callListId, candidates }: { callListId: number; candidates: Array<Candidate> }) {
         const response = await service.postCandidates(
            callListId,
            candidates.map((c) => ({ candidate_id: c.id, is_handled: false })),
         );
         return response.data;
      },
   },
};

export default CandidateStore;
